'use client';

import PropTypes from 'prop-types';
import { Box, Button, Card, CardActions, CardContent, Stack, Typography } from '@mui/material';
import {
  WEBSITE_PAGE_BACKGROUND_DARK,
  WEBSITE_PAGE_BACKGROUND_MUTED,
} from '@/consts/website-theme';

const CARD_ACTION_SX = {
  color: '#0a0a0a',
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  px: 1,
  py: 0.5,
  minWidth: 0,
  '&:hover': {
    bgcolor: 'transparent',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
};

export default function DealerCard({ dealer, city }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        bgcolor: WEBSITE_PAGE_BACKGROUND_MUTED,
      }}
    >
      <Box
        sx={{
          bgcolor: WEBSITE_PAGE_BACKGROUND_DARK,
          px: 3,
          py: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 160, md: 180 },
        }}
      >
        <Box
          component="img"
          src={dealer.logo}
          alt={dealer.logoAlt}
          sx={{
            maxHeight: { xs: 72, md: 88 },
            maxWidth: '85%',
            width: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, px: 2.5, pt: 2.5, pb: 1 }}>
        {city ? (
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              letterSpacing: '0.12em',
              color: 'text.secondary',
              lineHeight: 1.4,
              mb: 0.75,
            }}
          >
            {city}
          </Typography>
        ) : null}
        <Typography component="h3" variant="h6" sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.3 }}>
          {dealer.name}
        </Typography>
        <Stack spacing={0.5} sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
          <Typography variant="body2">{dealer.address}</Typography>
          <Typography variant="body2">
            <Box
              component="a"
              href={dealer.phoneHref}
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {dealer.phone}
            </Box>
          </Typography>
          {dealer.email ? <Typography variant="body2">{dealer.email}</Typography> : null}
        </Stack>
      </CardContent>

      <CardActions sx={{ px: 1.5, pb: 2, pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
        {dealer.website ? (
          <Button
            component="a"
            href={dealer.website}
            target="_blank"
            rel="noopener noreferrer"
            variant="text"
            sx={CARD_ACTION_SX}
          >
            Visit Website
          </Button>
        ) : null}
        {dealer.email ? (
          <Button component="a" href={`mailto:${dealer.email}`} variant="text" sx={CARD_ACTION_SX}>
            Email
          </Button>
        ) : null}
        <Button component="a" href={dealer.phoneHref} variant="text" sx={CARD_ACTION_SX}>
          Call
        </Button>
      </CardActions>
    </Card>
  );
}

DealerCard.propTypes = {
  dealer: PropTypes.shape({
    name: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    phoneHref: PropTypes.string.isRequired,
    email: PropTypes.string,
    website: PropTypes.string,
    logo: PropTypes.string.isRequired,
    logoAlt: PropTypes.string.isRequired,
  }).isRequired,
  city: PropTypes.string,
};
